// [LOGIN_REDIRECT_V1] 로그인 관문 (2026-11-02)
//
//  역할
//   - App.js 에서 <Routes> 전체를 감싸, 화면을 보여주기 전에 "들어올 자격"을 검사한다.
//   - 토큰 없음        → 로그인 팝업(App.js 의 LoginOverlay)을 자동으로 연다
//                         + 팝업을 닫아도 빈 화면 대신 「로그인이 필요합니다」 카드
//   - guest / basic    → 「직원 전용 서비스」 카드 + 로그아웃 버튼
//                         (서버도 propertiesReadGuard 로 막고 있어 어차피 빈 화면이었음)
//   - premium / admin  → 원래 화면 그대로
//
//  연관 파일
//   - App.js            : <LoginGate onLoginClick={() => setLoginOpen(true)}> 로 사용
//   - hooks/useAuthGuard: 토큰 "만료" 처리 담당 (이 파일은 토큰 "없음" 담당 — 역할 분리)
//   - api/axios.js·http.js : 401 이면 토큰 삭제 후 새로고침 → 새로고침 뒤 이 관문이 팝업을 띄움
//   - Header.jsx NAV_ITEMS : 메뉴별 등급 제한은 여전히 그쪽 (이 파일은 앱 입구만)
//
//  ★ 공개 화면을 새로 만들면 아래 PUBLIC_PREFIXES 에 추가할 것 (안 하면 로그인 팝업이 뜸)
import React, { useEffect } from "react";
import { useLocation } from "react-router-dom";

// 로그인 없이 열려야 하는 화면 (주소 앞부분 일치)
const PUBLIC_PREFIXES = [
  "/register", // 일반인 물건접수 (PublicIntake — SMS 인증으로 동작)
  "/auth/",    // 카카오 로그인 콜백 — 막으면 로그인 자체가 안 됨
  "/m/",       // 모바일 매물 상세 — 문자 본문에 들어가는 링크라 지금 동작 유지
];

// guest/basic 도 들어갈 수 있는 화면 (로그인은 했지만 직원이 아닌 경우)
const GUEST_OK_PREFIXES = ["/me"];   // 내 정보 — 등급 확인용

// 매물 등을 볼 수 있는 등급 (00 문서 §4 등급 체계)
const STAFF_GRADES = ["premium", "admin"];

// JWT 토큰 가운데 부분(payload)에서 등급(grade)을 꺼낸다.
//  - 실패하면 null → 아래에서 "막지 않음"으로 처리 (판단 못 하면 기존처럼 보여줌)
function readGrade(token) {
  try {
    const b64 = token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/");
    const payload = JSON.parse(atob(b64));
    return payload.grade || null;
  } catch (e) {
    return null;
  }
}

// 안내 카드 공통 모양
function Notice({ icon, title, desc, children }) {
  return (
    <div style={{ display: "flex", justifyContent: "center", padding: "60px 16px" }}>
      <div style={{
        maxWidth: 420, width: "100%", background: "#fff", borderRadius: 14,
        boxShadow: "0 4px 18px rgba(0,0,0,0.08)", padding: "32px 24px", textAlign: "center",
      }}>
        <div style={{ fontSize: 40, marginBottom: 10 }}>{icon}</div>
        <div style={{ fontSize: 19, fontWeight: 700, marginBottom: 8 }}>{title}</div>
        <div style={{ fontSize: 14, color: "#666", lineHeight: 1.6, marginBottom: 20 }}>{desc}</div>
        {children}
      </div>
    </div>
  );
}

const btnStyle = {
  padding: "10px 28px", fontSize: 15, fontWeight: 600, border: "none",
  borderRadius: 8, background: "#2563eb", color: "#fff", cursor: "pointer",
};

export default function LoginGate({ onLoginClick, children }) {
  const { pathname } = useLocation();               // 주소가 바뀔 때마다 다시 검사
  const token = localStorage.getItem("token");      // 로그인 여부 기준 (앱 전체 공통)
  const isPublic = PUBLIC_PREFIXES.some((p) => pathname.startsWith(p));
  const needLogin = !token && !isPublic;

  // 미로그인으로 보호 화면에 들어오면 로그인 팝업 자동 열기
  //  (훅은 아래 return 들보다 먼저 — React 규칙: 훅은 조건 없이 항상 같은 순서로 호출)
  useEffect(() => {
    if (needLogin && onLoginClick) onLoginClick();
  }, [needLogin, pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  // ① 공개 화면 → 검사 없이 통과
  if (isPublic) return children;

  // ② 토큰 없음 → 팝업을 닫았을 때 보일 안내 (빈 화면 방지)
  if (!token) {
    return (
      <Notice icon="🔒" title="로그인이 필요합니다"
              desc="모모부동산 업무 화면은 로그인 후 이용할 수 있습니다.">
        <button style={btnStyle} onClick={onLoginClick}>로그인</button>
      </Notice>
    );
  }

  // ③ 로그인했지만 직원 등급이 아님 (guest/basic) → 직원 전용 안내
  const grade = readGrade(token);
  const guestOk = GUEST_OK_PREFIXES.some((p) => pathname.startsWith(p));
  if (grade && !STAFF_GRADES.includes(grade) && !guestOk) {
    const logout = () => {
      localStorage.removeItem("token");   // 로그인 정보 삭제 (axios.js 401 처리와 같은 항목)
      localStorage.removeItem("user");
      window.location.href = "/";         // 새로고침 → 관문이 다시 로그인 팝업을 띄움
    };
    return (
      <Notice icon="🏢" title="직원 전용 서비스입니다"
              desc={"현재 계정 등급(" + grade + ")으로는 이용할 수 없습니다.\n" +
                    "직원이시면 관리자에게 등급 변경을 요청한 뒤 다시 로그인해 주세요."}>
        <div style={{ whiteSpace: "pre-line" }} />
        <button style={btnStyle} onClick={logout}>로그아웃</button>
      </Notice>
    );
  }

  // ④ premium / admin (또는 등급을 못 읽은 경우) → 원래 화면
  return children;
}
